import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import "./CSS/ChatInterface.css";
import SLPSelector from "./SLPSelector";
// import VoiceIcon from "../../../Assets/voice.svg";
import { io } from "socket.io-client";
import { FaArrowLeft, FaThumbsUp, FaThumbsDown } from "react-icons/fa";
import { MdSend } from "react-icons/md";
import { Player } from "@lottiefiles/react-lottie-player";
import robotAnimation from "../../../Assets/robot.json";
import { LayoutGroup, motion } from "framer-motion";
import SurveyModal from "./SurveyModal";
import { lightHaptic, mediumHaptic } from "../../../native/capacitor";
import useBackSwipe from "../../../hooks/useBackSwipe";
import { animateBackAndRun } from "../../../hooks/useBackSwipe";
import {
  consumeSyncedAiReplies,
  enqueueAiChatMessage,
} from "../../../offline/sessionOffline";

const ChatInterface = ({
  videoTime,
  videoDuration,
  newVideoUploaded,
  setNewVideoUploaded,
  seekToTime,
  videoId,
  profile,
  isOnline = true,
  isGuest,
  refreshTrigger,
  restoredChatMessages,
  onRequestVideoUpload,
  hasRespondedToIntro,
  setHasRespondedToIntro,
  slpConversations,
  setSlpConversations,
}) => {
  const [aiMessages, setAiMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState("");
  const [chatMode, setChatMode] = useState("ai");
  const [senderRole, setSenderRole] = useState("bot");
  const [messageQueue, setMessageQueue] = useState([]);
  const [context, setContext] = useState("base");
  const [isAwaitingResponse, setIsAwaitingResponse] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [parentName, setParentName] = useState("");
  const [childName, setChildName] = useState("");
  const [selectedSlp, setSelectedSlp] = useState(null);
  const [feedback, setFeedback] = useState({});
  const [busyEnding, setBusyEnding] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showSurvey, setShowSurvey] = useState(false);
  const [surveyData, setSurveyData] = useState({
    q1: null, q2: null, q3: null, q4: null, q5: ""
  });

  const processedMessages = useRef(new Set());
  const typingTimeoutRef = useRef(null);

  const chatBoxRef = useRef(null);
  const chatViewRef = useRef(null);

  const REACT_APP_API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const OPTION_BUTTONS = useMemo(
    () => [
      { label: "Upload Video", value: "Upload Video" },
      { label: "Self Reflection", value: "Self Reflection" },
      { label: "Receive Feedback", value: "Receive Feedback" },
      { label: "Let's Chat", value: "Let's Chat" },
    ],
    []
  );

  const displayedMessages = useMemo(() => {
    if (chatMode === "ai") return aiMessages;
    if (selectedSlp) return slpConversations[selectedSlp.id] || [];
    return [];
  }, [chatMode, aiMessages, selectedSlp, slpConversations]);

  useBackSwipe(
    () => setSelectedSlp(null),
    { enabled: chatMode === "slp" && Boolean(selectedSlp), target: chatViewRef }
  );

  const pushBotMessage = useCallback((msgObject) => {
    setMessageQueue(prev => [...prev, msgObject]);
  }, []);

  useEffect(() => {
    const socket = io(REACT_APP_API_BASE_URL, {});

    socket.on("new_assistant_message", (data) => {
      if (data.videoId === videoId) {
        const msg = { text: data.message, sender: "bot" };
        if (data.quickReplies) {
          msg.options = data.quickReplies.map((label) => ({
            label,
            value: label,
          }));
        }
        pushBotMessage(msg);
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [videoId, REACT_APP_API_BASE_URL, pushBotMessage]);

  const endSession = async () => {
    if (busyEnding) return;
    setBusyEnding(true);

    try {
      await fetch(`${REACT_APP_API_BASE_URL}/api/end-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          videoId,
          userId: profile?.id,
        }),
      });

      pushBotMessage({
        text: "Great! Your session has ended and your training summary is on its way. 🎉",
        sender: "bot",
      });
    } catch (err) {
      console.error("Failed to end session:", err);
      pushBotMessage({
        text: "Hmm, I couldn’t wrap things up just now — please try again.",
        sender: "bot",
      });
    } finally {
      setBusyEnding(false);
      setShowSurvey(true);
    }
  };

  useEffect(() => {
    if (isGuest) {
      const storedParentName = sessionStorage.getItem("guest_parent_name");
      const storedChildName = sessionStorage.getItem("guest_child_name");

      if (storedParentName) setParentName(storedParentName);
      if (storedChildName) setChildName(storedChildName);
    } else if (profile) {
      if (profile?.parent_name) setParentName(profile.parent_name);
      if (profile?.child_name) setChildName(profile.child_name);
    }
  }, [profile, isGuest, refreshTrigger]);

  const appendToSlp = useCallback((slpId, newMsg) => {
    setSlpConversations(prev => ({
      ...prev,
      [slpId]: [...(prev[slpId] || []), newMsg],
    }));
  }, [setSlpConversations]);

  useEffect(() => {
    if (chatMode === "ai" && aiMessages.length === 0) {
      const intro1 = {
        text: "Hey there! 👋 I’m PaiCoach — I'm here to assist you in your interaction with your child!\nWhat would you like to do next?",
        sender: "bot",
        options: OPTION_BUTTONS,
      };

      const intro2 = {
        text:
          `Whenever you feel you're done with this coaching session, just tap "End Session". I'll generate a concise summary you can revisit anytime from the Home page.`,
        sender: "bot",
        options: [
          {
            label: "View Session Summary Example",
            value: "View Session Summary Example",
          },
        ],
      };

      setAiMessages([intro1, intro2]);
    }
  }, [chatMode, aiMessages.length, OPTION_BUTTONS]);

  const SummaryModal = () => (
    <div className="summary-modal-overlay" onClick={() => setShowSummaryModal(false)}>
      <div className="summary-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Session Summary (Example)</h2>

        <h3>Reflection Summary</h3>
        <p>
          The user reflected positively on their ability to build connection and engage their child during preferred activities. They
          recognized the importance of tuning into their child's interests and responding to cues. The user identified a need to
          improve in gently redirecting their child's attention when engagement wanes to maintain effective interaction.
        </p>

        <h3>AI Feedback</h3>
        <p>
          Thank you for sharing your reflections. It's great to hear that you felt the interaction went well in terms of building
          connection and tuning into your child's interests. Recognizing what worked well, such as engaging your child during
          activities and responding to her cues, is key to continued progress. For improvements, focusing on maintaining your child's
          engagement and gently redirecting her attention when needed will further enhance your interactions. Keep practicing the
          model and pause technique to support her communication development effectively.
        </p>

        <h3>Self-Reflection Q&amp;A</h3>
        <ul>
          <li><strong>Q:</strong> What do you think went well during your interaction with your child?</li>
          <li><strong>A:</strong> I think I did a good job building connection by redirecting her focus when needed and engaging her during activities she likes, like being inside the tunnel.</li>
          <li><strong>Q:</strong> What do you think could have been improved in your interaction?</li>
          <li><strong>A:</strong> I could have been more mindful when she wasn’t engaged and tried to gently redirect her attention more effectively.</li>
        </ul>

        <button onClick={() => setShowSummaryModal(false)} className="modal-close-btn">Close</button>
      </div>
    </div>
  );

  useEffect(() => {
    if (!isAwaitingResponse && messageQueue.length > 0 && !isTyping) {
      setIsTyping(true);

      const nextMessage = messageQueue[0];
      typingTimeoutRef.current = setTimeout(() => {
        setAiMessages((prevMessages) => [...prevMessages, nextMessage]);
        lightHaptic();

        if (nextMessage.awaitResponse) {
          setIsAwaitingResponse(true);
        }

        setMessageQueue((prevQueue) => prevQueue.slice(1));
        setIsTyping(false);
      }, 1000);
    }
  }, [messageQueue, isAwaitingResponse, isTyping]);

  useEffect(() => {
    if (newVideoUploaded) {
      const buttonsWithoutUpload = OPTION_BUTTONS.filter(
        (b) => !b.value.toLowerCase().includes("upload")
      );
      const videoUploadMessage = {
        text: "Thank you for your effort in uploading another video about you and your child's interaction! 😊 \nWhat would you like to do next?",
        sender: senderRole,
        options: buttonsWithoutUpload,
      };
      if (!processedMessages.current.has(videoUploadMessage.text)) {
        processedMessages.current.add(videoUploadMessage.text);
        pushBotMessage(videoUploadMessage);
      }
      if (!parentName.trim() || !childName.trim()) {
        setContext("askParentName");
        const askParentMsg = {
          text: "Please enter the parent's name:",
          sender: senderRole,
        };
        pushBotMessage(askParentMsg);
        setIsAwaitingResponse(true);
      } else {
        setContext("base");
        return;
      }

      if (chatMode === "slp" && selectedSlp) {
        appendToSlp(selectedSlp.id, {
          text: "A new video has been uploaded (SLP mode).",
          sender: senderRole,
        });
      }
      setNewVideoUploaded(false);
    }
  }, [
    newVideoUploaded,
    setNewVideoUploaded,
    selectedSlp,
    chatMode,
    senderRole,
    parentName,
    childName,
    OPTION_BUTTONS,
    pushBotMessage,
    appendToSlp,
  ]);

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTo({
        top: chatBoxRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [displayedMessages, isTyping]);

  const handleSkipClick = async () => {
    if (chatMode === "slp") return;

    const skippedMessage = { text: "[Skipped]", sender: "user" };
    setAiMessages((prev) => [...prev, skippedMessage]);

    setIsAwaitingResponse(false);

    await fetchAndPushBotReply("self_reflection", "[Skipped]");
  }

  const handleSendButtonClick = async () => {
    await lightHaptic();
    await sendMessage();
  };

  const handleSkipButtonClick = async () => {
    await lightHaptic();
    await handleSkipClick();
  };

  const handleEndSessionClick = async () => {
    await mediumHaptic();
    await endSession();
  };

  const sendMessage = async () => {
    const userInput = currentMessage.trim();
    if (!userInput) return;

    const userMessage = { text: userInput, sender: "user" };
    if (!hasRespondedToIntro) setHasRespondedToIntro(true);
    if (chatMode === "ai") {
      setAiMessages((prev) => [...prev, userMessage]);
    } else {
      appendToSlp(selectedSlp.id, userMessage);
    }
    setCurrentMessage("");

    if (isAwaitingResponse && chatMode === "ai") {
      setIsAwaitingResponse(false);
    }

    if (chatMode === "slp" && selectedSlp) {
      setIsTyping(true);
      setTimeout(async () => {
        const slpReply = {
          text: "I have received your message and will respond soon!",
          sender: senderRole,
        };
        appendToSlp(selectedSlp.id, slpReply);
        setIsTyping(false);
        const conversationHistory = [
          { role: "user-slp", content: userInput },
          { role: "slp", content: slpReply.text },
        ];

        try {
          const userId = profile ? profile.id : null;
          await fetch(`${REACT_APP_API_BASE_URL}/api/slp-chat`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              videoId,
              conversationHistory,
              userId,
              slpId: selectedSlp?.id,
            }),
          });
        } catch (error) {
          console.error("Error sending SLP chat:", error);
        }
      }, 1000);
      return;
    }

    if (context === "askParentName") {
      setParentName(userInput);
      setIsTyping(true);

      setTimeout(() => {
        const askChildMsg = {
          text: "Please enter the child's name:",
          sender: senderRole,
        };
        pushBotMessage(askChildMsg);

        setContext("askChildName");
        setIsAwaitingResponse(true);
      }, 1000);

      return;
    } else if (context === "askChildName") {
      setChildName(userInput);
      setContext("base");
      return;
    } else if (context === "self_reflection") {
      await fetchAndPushBotReply("self_reflection", userInput);
    } else if (context === "feedback_wait" || context === "feedback_ready") {
      await fetchAndPushBotReply("feedback_followup", userInput);
    } else {
      await fetchAndPushBotReply("free_qa", userInput);
    }
  };

  const handleSelectSlp = (slp) => {
    setSelectedSlp(slp);
    setSenderRole("slp");

    setSlpConversations((prev) => {
      if (prev[slp.id]) return prev;
      const greeting = {
        text: `Hello, I'm ${slp.name}. Let me know how I can help you today!`,
        sender: "slp",
      };
      return { ...prev, [slp.id]: [greeting] };
    });
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60)
      .toString()
      .padStart(2, "0");
    return `${minutes}:${secs}`;
  };

  const [expandedMessages, setExpandedMessages] = useState({});
  const toggleExpandMessage = (index) => {
    setExpandedMessages((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleFeedback = (idx, value) => {
    setFeedback(prev => ({
      ...prev,
      [idx]: prev[idx] === value ? undefined : value,
    }));
  };

  useEffect(() => {
    if (restoredChatMessages && restoredChatMessages.length > 0) {
      setAiMessages(restoredChatMessages);
      console.log("✅ Restored chat applied.");
    }
  }, [restoredChatMessages]);

  useEffect(() => {
    if (!isOnline || !profile?.id || !videoId) return;
    const syncedReplies = consumeSyncedAiReplies({
      userId: profile.id,
      videoId,
    });
    if (!syncedReplies.length) return;

    syncedReplies.forEach((reply) => {
      const msg = {
        text: reply.botReply || "Your offline message has been synced.",
        sender: "bot",
      };
      if (reply.quickReplies?.length) {
        msg.options = reply.quickReplies.map((label) => ({
          label,
          value: label,
        }));
      }
      pushBotMessage(msg);
    });
  }, [isOnline, profile?.id, pushBotMessage, refreshTrigger, videoId]);

  const fetchAndPushBotReply = async (ctx, latestUser) => {
    const history = [
      ...aiMessages.map((m) => ({
        role: m.sender === "bot" ? "assistant" : "user",
        content: m.text,
      })),
      ...(latestUser ? [{ role: "user", content: latestUser }] : []),
    ];

    if (!isOnline && profile?.id && videoId) {
      enqueueAiChatMessage({
        userId: profile.id,
        videoId,
        context: ctx,
        conversationHistory: history,
        parentName,
        childName,
      });
      pushBotMessage({
        text: "Saved offline. I’ll send this when you’re back online.",
        sender: "bot",
      });
      return;
    }

    try {
      const res = await fetch(`${REACT_APP_API_BASE_URL}/api/ai-chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          videoId,
          conversationHistory: history,
          context: ctx,
          parentName,
          childName,
          userId: profile?.id,
        }),
      });
      const { botReply, needUpload, quickReplies, transferToSlp } = await res.json();

      if (transferToSlp) {
        pushBotMessage({
          text:
            botReply,
          sender: "bot",
        });

        setTimeout(() => {
          setChatMode("slp");
          setSenderRole("slp");
          setSelectedSlp(null);
        }, 5000);

        return;
      }

      if (needUpload) {
        pushBotMessage({
          text: botReply,
          sender: "bot",
          options: [{ label: "Upload Video", value: "Upload Video" }],
        });
      } else if (botReply) {
        const newMsg = { text: botReply, sender: "bot" };

        let extraReplies = [];

        if (ctx === "self_reflection") {
          const lower = botReply.toLowerCase();
          if (
            lower.includes("request feedback") ||
            lower.includes("click the \"receive feedback\"")
          ) {
            extraReplies = [{ label: "Receive Feedback", value: "Receive Feedback" }];
          } else {
            extraReplies = [{ label: "Skip Question", value: "Skip Question" }];
          }
        }

        if (quickReplies || extraReplies.length > 0) {
          newMsg.options = [
            ...(quickReplies?.map((label) => ({ label, value: label })) || []),
            ...extraReplies,
          ];
        }
        pushBotMessage(newMsg);
      }

    } catch (err) {
      console.error(err);
    }
  };

  const handleOptionClick = async (value) => {
    await lightHaptic();
    if (value === "Skip Question") {
      handleSkipClick();
      return;
    }
    if (value === "View Session Summary Example") {
      setShowSummaryModal(true);
      return;
    }
    if (value !== "Upload Video") {
      setAiMessages((p) => [...p, { text: value, sender: "user" }]);
    }
    setIsAwaitingResponse(false);
    if (value === "Self Reflection") {
      await fetchAndPushBotReply("self_reflection", value);
      setContext("self_reflection");
    } else if (value === "Receive Feedback") {
      await fetchAndPushBotReply("feedback_request", value);
      setContext("feedback_wait");
    } else if (value === "Let's Chat") {
      setContext("free_qa");
      pushBotMessage({ text: "Great - ask me anything!", sender: "bot" });
    } else if (value === "Upload Video") {
      onRequestVideoUpload?.();
    } else {
      await fetchAndPushBotReply(context, value);
    }
  };

  const lastBotIndex = displayedMessages
    .map((m, i) => (m.sender === "bot" ? i : -1))
    .reduce((max, i) => (i > max ? i : max), -1);

  const submitSurvey = async () => {
    try {
      await fetch(`${REACT_APP_API_BASE_URL}/api/session-survey`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: videoId,
          userId: profile?.id,
          answers: surveyData,
        }),
      });
      setShowSurvey(false);
      pushBotMessage({ text: "Thanks for the feedback! 🌟", sender: "bot" });
    } catch (err) {
      console.error(err);
      alert("Couldn’t save survey — please try again.");
    }
  };

  return (
    <div className="chat-interface" ref={chatViewRef}>
      {showSurvey && (
        <SurveyModal
          surveyData={surveyData}
          setSurveyData={setSurveyData}
          onClose={() => setShowSurvey(false)}
          onSubmit={submitSurvey}
        />
      )}
      <div className="chat-mode-toggle">
        <input
          type="radio"
          id="chat-ai"
          name="chatMode"
          value="ai"
          checked={chatMode === "ai"}
          onChange={() => {
            setChatMode("ai");
            setSenderRole("bot");
          }}
        />
        <label htmlFor="chat-ai">Chat with AI</label>

        {/* <input
          type="radio"
          id="chat-slp"
          name="chatMode"
          value="slp"
          checked={chatMode === "slp"}
          onChange={() => {
            setChatMode("slp");
            setSenderRole("slp");
            setSelectedSlp(null);
          }}
        />
        <label htmlFor="chat-slp">Chat with Expert</label> */}
      </div>
      {videoId && chatMode === "ai" && (<button
        className="fixed-options-buttons end-session-btn"
        onClick={handleEndSessionClick}
        disabled={busyEnding}
      >
        {busyEnding ? "Ending…" : "End Session"}
      </button>
      )}
      {chatMode === "slp" && !selectedSlp ? (
        <SLPSelector onSelect={handleSelectSlp} />
      ) : (
        <>
          {chatMode === "slp" && selectedSlp && (
            <div className="slp-chat-header">
              <button
                className="back-button-slp"
                onClick={() =>
                  animateBackAndRun({
                    target: chatViewRef,
                    onBack: () => setSelectedSlp(null),
                  })
                }
              >
                <FaArrowLeft className="back-icon" />
                Back
              </button>
              <span className="slp-name-header">{selectedSlp.name}</span>
            </div>
          )}
          <LayoutGroup id="chat">
            <div className="chat-box" ref={chatBoxRef}>
              {displayedMessages.map((msg, index, arr) => {
                const isLastBot = !isTyping && index === lastBotIndex;
                const isLastBotInGroup =
                  msg.sender === "bot" &&
                  (index === displayedMessages.length - 1 || displayedMessages[index + 1]?.sender !== "bot");


                return (
                  <div key={index} className={`chat-message ${msg.sender} chat-message-appear`}>
                    {isLastBot && (
                      <motion.div
                        layoutId="bot-avatar"
                        className="bot-avatar"
                        transition={{ type: "spring", stiffness: 200, damping: 35 }}
                      >
                        <Player autoplay loop src={robotAnimation} />
                      </motion.div>
                    )}

                    {msg.sender === "slp" && (
                      <div className="slp-chat-avatar-container">
                        <img
                          src={selectedSlp?.avatar}
                          alt={selectedSlp?.name}
                          className="slp-chat-avatar"
                        />
                      </div>
                    )}
                    <div
                      className={`message-content ${msg.sender === "user" ? "user" : msg.sender
                        } ${msg.sender === "user" && chatMode === "slp" ? "user-slp" : ""}`}
                    >
                      {msg.sender === "bot" && msg.timestamp && (
                        <span className="message-timestamp">
                          [
                          <button onClick={() => seekToTime(msg.timestamp)}>
                            {formatTime(msg.timestamp)}
                          </button>
                          ]
                        </span>
                      )}

                      <span className="message-text">
                        {msg.text.length > 500 && !expandedMessages[index] ? (
                          <>
                            {msg.text.substring(0, 210)}...{" "}
                            <button
                              className="toggle-expand-btn"
                              onClick={() => toggleExpandMessage(index)}
                            >
                              Show More
                            </button>
                          </>
                        ) : msg.text.length > 210 && expandedMessages[index] ? (
                          <>
                            {msg.text}{" "}
                            <button
                              className="toggle-expand-btn"
                              onClick={() => toggleExpandMessage(index)}
                            >
                              Show Less
                            </button>
                          </>
                        ) : (
                          msg.text
                        )}
                      </span>
                      {msg.options && (
                        <div className="options-row">
                          {msg.options.map((o) => (
                            <button
                              key={o.value}
                              className="options-buttons"
                              onClick={() => handleOptionClick(o.value)}
                            >
                              {o.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {isLastBotInGroup && (
                      <div className="feedback-side">
                        <button
                          className={`thumb-btn ${feedback[index] === "up" ? "active" : ""}`}
                          onClick={() => handleFeedback(index, "up")}
                          aria-label="Thumbs up"
                        >
                          <FaThumbsUp />
                        </button>
                        <button
                          className={`thumb-btn ${feedback[index] === "down" ? "active" : ""}`}
                          onClick={() => handleFeedback(index, "down")}
                          aria-label="Thumbs down"
                        >
                          <FaThumbsDown />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              {isTyping && !isAwaitingResponse && (
                <div className="typing-indicator">
                  <motion.div
                    layoutId="bot-avatar"
                    className="bot-avatar"
                    transition={{ type: "spring", stiffness: 200, damping: 35 }}
                  >
                    <Player
                      autoplay
                      loop
                      src={robotAnimation}
                    />
                  </motion.div>
                  <span className="dot" /><span className="dot" /><span className="dot" />
                </div>
              )}

            </div>
          </LayoutGroup>
          {chatMode === "ai" && hasRespondedToIntro && (
            <div className="fixed-options-row">
              <button className="fixed-options-buttons" onClick={() => handleOptionClick("Self Reflection")}>
                Self Reflection
              </button>
              <button className="fixed-options-buttons" onClick={() => handleOptionClick("Receive Feedback")}>
                Receive Feedback
              </button>
              <button className="fixed-options-buttons" onClick={() => handleOptionClick("Let's Chat")}>
                Let's Chat
              </button>
            </div>
          )}
          <div className="input-container">
            <input
              type="text"
              placeholder={
                chatMode === "ai"
                  ? "Ask me anything!"
                  : selectedSlp
                    ? `Message ${selectedSlp.name}...`
                    : "Select an SLP first..."
              }
              value={currentMessage}
              onChange={(e) => setCurrentMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") sendMessage();
              }}
              disabled={chatMode === "slp" && !selectedSlp}
            />

            {chatMode !== "slp" && (
              <button className="skip-button" onClick={handleSkipButtonClick}>
                Skip
              </button>
            )}

            <button className={`send-button ${chatMode === "slp" ? "send-slp" : "send-ai"}`} onClick={handleSendButtonClick} disabled={chatMode === "slp" && !selectedSlp}>
              <MdSend size={20} />
            </button>
          </div>
        </>
      )}
      {showSummaryModal && <SummaryModal />}
    </div>
  );
};

export default ChatInterface;
